import { useEffect, useRef, useState } from 'react'
import { Alert, Anchor, Box, Button, Group, Paper, PasswordInput, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { auth, renderTurnstile } from '@chattee.ai/auth'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import backend from '../../backend/backend'
import { homeFor } from '../../components/shared/Guards'
import Message from '../../i18n/Message'
import { useT } from '../../hooks'
import { signIn } from '../../store/authActionCreators'

export default function Login() {
  const t = useT()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const status = useSelector((s) => s.auth.status)
  const user = useSelector((s) => s.auth.user)
  const expired = useSelector((s) => s.auth.expiredNotice)
  const mfa = useSelector((s) => s.auth.mfa)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [cfg, setCfg] = useState({})
  const [needsSetup, setNeedsSetup] = useState(false)
  const box = useRef(null)
  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: { email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : t('error_email')), password: (v) => (v ? null : t('error_required')) },
  })

  useEffect(() => {
    auth.getConfig().then(setCfg).catch(() => {})
    backend.getSetupStatus().then((s) => setNeedsSetup(!!s.needsOwnerSetup)).catch(() => {})
  }, [])

  if (status === 'ready' && user) return <Navigate to={homeFor(user.role)} replace />
  if (status === 'mfa') return <Navigate to={mfa.enrolled ? '/login/verify' : '/security/setup'} replace />

  const submit = async (values) => {
    setBusy(true)
    setError(null)
    try {
      const token = cfg.turnstileSiteKey && box.current ? await renderTurnstile(box.current, cfg.turnstileSiteKey) : undefined
      const step = await dispatch(signIn(values.email.trim(), values.password, token))
      if (step === 'verify') navigate('/login/verify')
      else if (step === 'enrol') navigate('/security/setup')
    } catch (err) {
      // Platform sign-in errors stay vague on purpose; only a gate refusal is worded differently.
      setError(err?.code === 'not_staff' ? 'login_no_access' : 'login_failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing={0} mih="100vh">
      <Stack justify="center" p="xl" maw={440} w="100%" mx="auto">
        <Stack gap={4}>
          <Title order={1} size="h2">
            <Message id="app_name" />
          </Title>
          <Text c="dimmed">
            <Message id="login_subtitle" />
          </Text>
        </Stack>
        {expired && <Alert color="orange"><Message id="signed_out_inactive" /></Alert>}
        {error && <Alert color="red"><Message id={error} /></Alert>}
        <form onSubmit={form.onSubmit(submit)} noValidate>
          <Stack>
            <TextInput label={t('field_email')} type="email" autoComplete="username" {...form.getInputProps('email')} />
            <PasswordInput label={t('field_password')} autoComplete="current-password" {...form.getInputProps('password')} />
            <div ref={box} />
            <Button type="submit" loading={busy} fullWidth>
              <Message id="sign_in" />
            </Button>
          </Stack>
        </form>
        <Group justify="space-between">
          {cfg.emailAvailable ? (
            <Anchor component={Link} to="/forgot-password" size="sm">
              <Message id="forgot_password" />
            </Anchor>
          ) : (
            <span />
          )}
          {needsSetup && (
            <Anchor component={Link} to="/setup" size="sm">
              <Message id="setup_link" />
            </Anchor>
          )}
        </Group>
      </Stack>
      <Box bg="graphite.8" c="white" p="xl" visibleFrom="md" style={{ display: 'flex', alignItems: 'center' }}>
        <Stack maw={420} gap="lg">
          <Title order={2} c="white" ff="var(--mantine-font-family-headings)">
            <Message id="app_name" />
          </Title>
          <Text c="graphite.2">
            <Message id="brand_by" />
          </Text>
          <Stack gap="md">
            {['brand_point_1', 'brand_point_2', 'brand_point_3'].map((id) => (
              <Paper key={id} p="md" bg="graphite.7" withBorder={false}>
                <Text c="graphite.1" size="sm">
                  <Message id={id} />
                </Text>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </Box>
    </SimpleGrid>
  )
}
